import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';
import { Skeleton } from "ui";
import { cn } from 'utils/cn';
import { useAppSelector } from '../../../../store';
import { getIsOpeningNewTask, getSelectedTask, getShouldAnimateComments, getShouldShowSkeleton } from '../../../../store/tasksSlice';
import { Comment } from "./comment";
import { Update } from "./update";

export const Activity = () => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const task = useAppSelector(getSelectedTask);
    const shouldAnimateComments = useAppSelector(getShouldAnimateComments);
    const shouldShowSkeleton = useAppSelector(getShouldShowSkeleton);
    const isOpeningNewTask = useAppSelector(getIsOpeningNewTask);
    const hasComments = Array.isArray(task?.comments) && task?.comments.length > 0;



    useEffect(() => {
        if (containerRef.current && task?.comments?.length) {
            setTimeout(() => {
                // Only scroll if the container has scrollable content
                if (containerRef.current && containerRef.current.scrollHeight > containerRef.current.clientHeight) {
                    containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' });
                }
            }, 100);
        }
    }, [task?.comments]);

    if (shouldShowSkeleton) {
        return (
            <div className="flex flex-col gap-4 max-h-[250px] overflow-y-auto">
                <div className="flex items-center justify-center">
                    <div className="flex w-full gap-2">
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.4,
                                ease: [0.4, 0, 0.2, 1],
                                delay: 0
                            }}
                        >
                            <Skeleton className="size-9 rounded-md" />
                        </motion.div>
                        <div className="relative flex w-full flex-col gap-3 overflow-hidden">
                            <div className="flex items-center justify-between">
                                <motion.div
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: [0.4, 0, 0.2, 1],
                                        delay: 0.1
                                    }}
                                >
                                    <Skeleton className="h-5 w-24 rounded-md" />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: [0.4, 0, 0.2, 1],
                                        delay: 0.2
                                    }}
                                >
                                    <Skeleton className="h-5 w-24 rounded-md" />
                                </motion.div>
                            </div>
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.4,
                                    ease: [0.4, 0, 0.2, 1],
                                    delay: 0.3
                                }}
                            >
                                <Skeleton className="h-5 w-40 rounded-md" />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            id="task-comments-container"
            className={cn(
                'flex flex-col gap-4 max-h-[480px] overflow-y-auto overflow-x-hidden transition-all duration-300 ease-out',
                !hasComments && 'hidden'
            )}
            ref={containerRef}
            data-scrollable
            style={{ WebkitOverflowScrolling: 'touch' }}
            onWheel={(e) => e.stopPropagation()}
        >
            {task?.comments?.map((comment, index, array) => {
                if (!comment) return null;

                // Use updated_at to ensure re-renders when comment data changes
                const componentKey = `${comment.id}-${comment.updated_at}`;

                const CommentComponent = comment.comment_type === "task_update" ? (
                    <Update key={componentKey} details={comment} />
                ) : (
                    <Comment key={componentKey} details={comment} />
                );

                // Check if this is an optimistic comment that should animate
                const isOptimisticComment = typeof comment.id === 'string' && comment.id.includes('OPTIMISTIC');
                const shouldAnimateOptimistic = shouldAnimateComments && isOptimisticComment;

                // Check if we should stagger comments (only when opening a new existing task)
                const shouldStaggerAll = shouldAnimateComments && !isOptimisticComment && isOpeningNewTask;

                // Calculate stagger delay based on comment position
                let staggerDelay = 0;
                if (shouldStaggerAll) {
                    const totalComments = array.length;
                    if (totalComments > 8) {
                        // If more than 8 comments, only stagger the last 8
                        const startStaggerIndex = totalComments - 8;
                        if (index >= startStaggerIndex) {
                            // Calculate delay relative to the start of the staggered section
                            staggerDelay = (index - startStaggerIndex) * 0.1;
                        }
                    } else {
                        // If 8 or fewer comments, stagger all
                        staggerDelay = index * 0.1;
                    }
                }

                if (shouldAnimateOptimistic) {
                    // Animate only optimistic comments (existing logic)
                    return (
                        <AnimateIn key={componentKey} keyId={componentKey}>
                            {CommentComponent}
                        </AnimateIn>
                    );
                } else if (shouldStaggerAll) {
                    // Stagger comments when opening a new existing task
                    return (
                        <AnimateIn key={componentKey} keyId={componentKey} delay={staggerDelay}>
                            {CommentComponent}
                        </AnimateIn>
                    );
                } else {
                    // No animation for existing comments in normal cases
                    return CommentComponent;
                }
            })}
        </div>
    );
};

const AnimateIn = ({
    children,
    keyId,
    delay = 0
}: {
    children: React.ReactNode;
    keyId: string;
    delay?: number;
}) => {

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={keyId}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1], delay }}
                className='flex flex-col gap-2'
            >
                {children}
            </motion.div>
        </AnimatePresence>
    );
};

export default Activity;